import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { MultiBackend, TouchTransition } from 'react-dnd-multi-backend';
import GameField, { GameFieldHandle } from './components/GameField';
import ElementsListButton from './components/ElementsListButton';
import Modal from './components/core/Modal';
import Card from './components/core/Card';
import ElementsList from './components/ElementsList';
import OAuthCallback from './components/auth/OAuthCallback';
// import CustomDragLayer from './components/CustomDragLayer';
import { useMobileDetect, useElementCombination } from './hooks';
import { Element } from './types';
import * as apiService from './services/apiService';
import UserMenu from './components/auth/UserMenu';
import './App.css';

// Настройки для MultiBackend (поддержка перетаскивания на десктопе и мобильных)
const DND_OPTIONS = {
  backends: [
    {
      backend: HTML5Backend,
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      transition: TouchTransition,
      delayTouchStart: 200,
      touchSlop: 0,
      ignoreContextMenu: true,
    }
  ]
};

const GameApp: React.FC = () => {
  const [elements, setElements] = useState<Element[]>([]);
  const isMobile = useMobileDetect();
  const [showElementsPopup, setShowElementsPopup] = useState(false);
  const gameFieldRef = useRef<GameFieldHandle>(null);
  
  const { handleCombineElements, isLoading } = useElementCombination(elements, setElements);

  useEffect(() => {
    const loadElements = async () => {
      try {
        const basicElements = await apiService.getBasicElements();
        if (basicElements.length > 0) {
          setElements(basicElements);
        }
      } catch (error) {
        console.error('Ошибка при загрузке элементов:', error);
      }
    };

    loadElements();
  }, []);

  // Добавляем эффект для корректировки высоты на мобильных устройствах
  useEffect(() => {
    if (isMobile) {
      // Функция для установки корректной высоты
      const setAppHeight = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
      
      // Устанавливаем высоту при загрузке
      setAppHeight();
      
      // Обновляем высоту при изменении размера или ориентации
      window.addEventListener('resize', setAppHeight);
      window.addEventListener('orientationchange', setAppHeight);
      
      return () => {
        window.removeEventListener('resize', setAppHeight);
        window.removeEventListener('orientationchange', setAppHeight);
      };
    }
  }, [isMobile]);

  const handleElementClick = (element: Element) => {
    if (gameFieldRef.current) {
      gameFieldRef.current.addElementToRandomPosition(element);
      if (isMobile) {
        setShowElementsPopup(false);
      }
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-content">
          <div className="app-header-title">
            <h1>Alchimio</h1>
            <p className="app-subtitle">Игра алхимия - соединяйте элементы и создавайте новые!</p>
            <p className="app-subtitle-small">(на базе искусственного интеллекта)</p>
          </div>
          <div className="app-header-auth">
            <UserMenu />
          </div>
        </div>
      </header>
      
      <main className="app-content">
        <div className="game-field-container">
          <GameField 
            ref={gameFieldRef}
            elements={elements}
            onCombine={handleCombineElements}
            isLoading={isLoading}
          />
        </div>
        
        {/* Десктопная версия списка элементов */}
        {!isMobile && (
          <Card title="Доступные элементы" className="desktop-elements-card">
            <ElementsList 
              elements={elements}
              onElementClick={handleElementClick}
            />
          </Card>
        )}
      </main>

      {/* Мобильная кнопка вызова списка */}
      {isMobile && (
        <ElementsListButton onClick={() => setShowElementsPopup(true)} />
      )}

      {/* Модальное окно для мобильной версии */}
      <Modal 
        isOpen={showElementsPopup} 
        onClose={() => setShowElementsPopup(false)}
        title="Доступные элементы"
      >
        <div className="mobile-elements-list">
          <ElementsList 
            elements={elements}
            onElementClick={handleElementClick}
          />
        </div>
      </Modal>

      {/* {isMobile && <CustomDragLayer />} */}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <DndProvider backend={MultiBackend} options={DND_OPTIONS}>
        <Routes>
          <Route path="/" element={<GameApp />} />
          <Route path="/auth/vk/callback" element={<OAuthCallback provider="vk" />} />
          <Route path="/auth/yandex/callback" element={<OAuthCallback provider="yandex" />} />
        </Routes>
      </DndProvider>
    </BrowserRouter>
  );
};

export default App; 